/** 'inbound' is a ticket Victory Live started by emailing us first. */
export type VlSupportTicketType = 'barcode_sync' | 'not_populating' | 'other' | 'need_pid' | 'inbound';

export const SUPPORT_TYPE_LABELS: Record<VlSupportTicketType, string> = {
  barcode_sync: 'Help Syncing Barcodes',
  not_populating: 'Not Populating',
  other: 'Other',
  need_pid: 'Need PID',
  inbound: 'From Victory Live',
};

export type VlSupportTicketRow = {
  vst_id: string;
  vst_type: VlSupportTicketType;
  vst_marketplaces?: string[] | null;
  vst_subject: string;
  vst_owner_initials?: string | null;
  /** Everyone in charge of the ticket ("Team" or people's initials). */
  vst_owners?: string[] | null;
  /** Tickets Victory Live started: who wrote, and what the team linked afterwards. */
  vst_contact_email?: string | null;
  vst_contact_name?: string | null;
  vst_user_id?: string | null;
  vst_event_id?: string | null;
  vst_status: 'open' | 'completed';
  vst_opened_at: string;
  vst_completed_at?: string | null;
  vst_reopen_count?: number | null;
  vst_open_seconds_total?: number | string | null;
  vst_current_open_since?: string | null;
  vst_event_name?: string | null;
  vst_event_date?: string | null;
  vst_pid?: string | null;
  vst_section?: string | null;
  vst_row?: string | null;
  vst_seats?: string | null;
  vst_last_message_at?: string | null;
  vst_last_inbound_at?: string | null;
  vst_unread_count?: number | null;
  vst_sync_accounts?: Array<{
    eventName?: string | null;
    method: 'season_seat_manager' | 'purchase_manager';
    team: string | null;
    platform: string | null;
  }> | null;
};

/** Seconds a ticket has been open, summed across reopen cycles. */
export const ticketOpenSeconds = (t: VlSupportTicketRow, now: Date = new Date()): number => {
  const base = Number(t.vst_open_seconds_total || 0);
  const since = t.vst_current_open_since ? new Date(t.vst_current_open_since).getTime() : NaN;
  return Number.isFinite(since) ? base + Math.max(0, Math.round((now.getTime() - since) / 1000)) : base;
};

/** 90 -> "1m", 3720 -> "1h 2m", 93600 -> "1d 2h". */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${Math.max(m, s > 0 ? 1 : 0)}m`;
};

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** "Oct 29, 2026, 3:00 PM": date, year and time, for when Victory Live first wrote to us. */
export const formatFullDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export type SupportTypeFilter = 'all' | VlSupportTicketType;
export type SupportOwnerFilter = 'company' | 'mine' | string;

export type SupportTicketUser = { id: string; name: string };

/**
 * Users behind each support ticket, from the listings it was raised on. A bulk ticket
 * can cover several users, so each ticket gets a list (alphabetical, no duplicates).
 */
export function ticketUsersFromListings(
  listings: Array<{
    u_id?: string | null;
    u_first_name?: string | null;
    u_last_name?: string | null;
    vl_support_requests?: Array<{ vsr_ticket_id?: string | null }> | null;
  }>,
): Record<string, SupportTicketUser[]> {
  const byTicket = new Map<string, Map<string, SupportTicketUser>>();
  for (const l of listings) {
    if (!l.u_id) continue;
    const name = `${l.u_first_name || ''} ${l.u_last_name || ''}`.trim() || 'Unknown user';
    for (const r of l.vl_support_requests || []) {
      if (!r.vsr_ticket_id) continue;
      const users = byTicket.get(r.vsr_ticket_id) || new Map<string, SupportTicketUser>();
      users.set(l.u_id, { id: l.u_id, name });
      byTicket.set(r.vsr_ticket_id, users);
    }
  }
  return Object.fromEntries(
    Array.from(byTicket.entries()).map(([id, users]) => [id, Array.from(users.values()).sort((a, b) => a.name.localeCompare(b.name))]),
  );
}

/** Listing statuses that count as sold. */
export const SOLD_LISTING_STATUSES = ['Sold', 'PaidManuallyViaQA'];

/**
 * A user's support tickets from their listings that have a support request. A ticket is
 * "sold" when every one of this user's listings on it is sold.
 */
export function userSupportScope(
  listings: Array<{ tp_status?: string | null; vl_support_requests?: Array<{ vsr_ticket_id?: string | null }> | null }>,
  /** Tickets linked to this user by hand (ones Victory Live started). */
  linkedTicketIds: string[] = [],
): { ticketIds: string[]; soldTicketIds: string[] } {
  const byTicket = new Map<string, { total: number; sold: number }>();
  for (const listing of listings) {
    const sold = SOLD_LISTING_STATUSES.includes(String(listing.tp_status || ''));
    for (const id of new Set((listing.vl_support_requests || []).map((r) => r.vsr_ticket_id).filter(Boolean) as string[])) {
      const entry = byTicket.get(id) || { total: 0, sold: 0 };
      entry.total += 1;
      if (sold) entry.sold += 1;
      byTicket.set(id, entry);
    }
  }
  const soldTicketIds = Array.from(byTicket.keys()).filter((id) => byTicket.get(id)!.sold === byTicket.get(id)!.total);
  const ticketIds = Array.from(new Set([...byTicket.keys(), ...linkedTicketIds.filter(Boolean)]));
  return { ticketIds, soldTicketIds };
}

/**
 * One search box over what people look for: the email subject, the event, the user, the production ID, the
 * type and the owner (plus who wrote and the marketplaces). Every word must appear somewhere, in any order.
 */
export function matchesSupportSearch(
  t: VlSupportTicketRow,
  users: Array<{ name: string }> = [],
  query: string,
): boolean {
  const words = query.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (!words.length) return true;
  const owners = t.vst_owners && t.vst_owners.length ? t.vst_owners : t.vst_owner_initials ? [t.vst_owner_initials] : [];
  const haystack = [
    t.vst_subject,
    t.vst_event_name,
    t.vst_event_date,
    t.vst_pid,
    t.vst_type,
    SUPPORT_TYPE_LABELS[t.vst_type],
    ...owners,
    ...users.map((u) => u.name),
    t.vst_contact_name,
    t.vst_contact_email,
    ...(t.vst_marketplaces || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/**
 * While a ticket's conversation is open, the list must not reshuffle under it. Opening a ticket marks it read,
 * and the normal order (unread first) would then move it, often onto another page, which closed the thread.
 * So rows keep the order they had when the ticket was opened; new rows go at the end. The open ticket stays
 * listed even if a filter would now hide it.
 */
export function keepOrderWhileOpen<T extends { vst_id: string }>(
  fresh: T[],
  frozenIds: string[] | null,
  selectedId: string | null,
  allRows: T[] = fresh,
): T[] {
  if (!selectedId || !frozenIds) return fresh;
  const byId = new Map(fresh.map((r) => [r.vst_id, r]));
  const selected = byId.get(selectedId) || allRows.find((r) => r.vst_id === selectedId);
  if (selected) byId.set(selectedId, selected);
  const kept = frozenIds.filter((id) => byId.has(id)).map((id) => byId.get(id) as T);
  const seen = new Set(frozenIds);
  const added = fresh.filter((r) => !seen.has(r.vst_id));
  return [...kept, ...added];
}

/** Words that say nothing about which event it is ("Lakers vs. Warriors", "Lakers at Warriors"). */
const SEARCH_FILLER = new Set(['vs', 'v', 'at', 'and', 'the']);

const searchWords = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^\p{L}\p{N}@.]+/u)
    .map((w) => w.replace(/^\.+|\.+$/g, ''))
    .filter((w) => w && !SEARCH_FILLER.has(w));

/** User search for linking: every word must appear in the name or email, in any order. */
export function userLinkSearchWhere(text: string): Record<string, unknown> | null {
  const words = searchWords(text);
  if (!words.length) return null;
  return {
    _and: [
      { u_deleted_at: { _is_null: true } },
      ...words.map((w) => ({ _or: [{ u_full_name: { _ilike: `%${w}%` } }, { u_email_id: { _ilike: `%${w}%` } }] })),
    ],
  };
}

/** Event search for linking: every word must appear in the name, brand or city, in any order. */
export function eventLinkSearchWhere(text: string): Record<string, unknown> | null {
  const words = searchWords(text);
  if (!words.length) return null;
  return {
    _and: [
      { e_deleted_at: { _is_null: true } },
      ...words.map((w) => ({
        _or: [{ e_name: { _ilike: `%${w}%` } }, { e_brand_name: { _ilike: `%${w}%` } }, { e_city: { _ilike: `%${w}%` } }],
      })),
    ],
  };
}

/** A ticket Victory Live started and nobody has linked to a user or an event yet. */
export const isUnlinked = (t: Pick<VlSupportTicketRow, 'vst_type' | 'vst_user_id' | 'vst_event_id'>): boolean =>
  t.vst_type === 'inbound' && !t.vst_user_id && !t.vst_event_id;

/** Adds the users that were linked to tickets by hand to the users found through listings. */
export function addLinkedUsers(
  byTicket: Record<string, SupportTicketUser[]>,
  links: Array<{ ticketId: string; userId: string }>,
  users: Array<{ u_id: string; u_first_name?: string | null; u_last_name?: string | null }>,
): Record<string, SupportTicketUser[]> {
  const names = new Map(users.map((u) => [u.u_id, `${u.u_first_name || ''} ${u.u_last_name || ''}`.trim() || 'Unknown user']));
  const out: Record<string, SupportTicketUser[]> = { ...byTicket };
  for (const { ticketId, userId } of links) {
    const name = names.get(userId);
    if (!name) continue;
    const current = out[ticketId] || [];
    if (current.some((u) => u.id === userId)) continue;
    out[ticketId] = [...current, { id: userId, name }].sort((a, b) => a.name.localeCompare(b.name));
  }
  return out;
}

/** Completed tickets stay on the Completed tab this many days after they were completed. */
export const COMPLETED_VISIBLE_DAYS = 30;

/** Earliest completion time still shown on the Completed tab. Display only: nothing is deleted, so Insights keeps its history. */
export const completedVisibleSince = (now: Date = new Date()): string =>
  new Date(now.getTime() - COMPLETED_VISIBLE_DAYS * 24 * 3600 * 1000).toISOString();

/** True when an open ticket is waiting on us: Victory Live replied last, or a reply is still unread. */
export const isWaitingOnUs = (t: VlSupportTicketRow): boolean =>
  t.vst_status === 'open' && (needsReply(t) || Number(t.vst_unread_count || 0) > 0);

/** Hasura where-clause for the Support Tickets table. */
export function supportTicketsWhere(options: {
  /** 'sold' has no status clause: it is every ticket in `ticketIds`, whether open or completed. */
  status: 'open' | 'completed' | 'sold';
  type: SupportTypeFilter;
  ownerInitials: string | null;
  search: string;
  /** Limit to these support tickets (a user's tickets on the user profile). */
  ticketIds?: string[] | null;
  /** Clock for the 30-day window on completed tickets (tests). */
  now?: Date;
}): Record<string, unknown> {
  const and: Record<string, unknown>[] = options.status === 'sold' ? [] : [{ vst_status: { _eq: options.status } }];
  if (options.ticketIds) and.push({ vst_id: { _in: options.ticketIds } });
  if (options.type !== 'all') and.push({ vst_type: { _eq: options.type } });
  if (options.ownerInitials) and.push({ vst_owners: { _contains: [options.ownerInitials] } });
  if (options.status === 'completed') and.push({ vst_completed_at: { _gte: completedVisibleSince(options.now) } });
  const q = options.search.trim();
  if (q) {
    const like = `%${q}%`;
    and.push({
      _or: [
        { vst_event_name: { _ilike: like } },
        { vst_pid: { _ilike: like } },
        { vst_subject: { _ilike: like } },
      ],
    });
  }
  return { _and: and };
}

/** The six marketplaces we support-ticket Victory Live about, with their logo ids. */
export const SUPPORT_MARKETPLACES: Array<{ name: string; logoId: string; color: string }> = [
  { name: 'StubHub', logoId: 'stubhub', color: '#6b2fd6' },
  { name: 'SeatGeek', logoId: 'seatgeek', color: '#ff5b49' },
  { name: 'Ticketmaster', logoId: 'ticketmaster', color: '#026cdf' },
  { name: 'GameTime', logoId: 'gametime', color: '#1fbd6b' },
  { name: 'Vivid Seats', logoId: 'vividseats', color: '#e0287a' },
  { name: 'TickPick', logoId: 'tickpick', color: '#18a0fb' },
];

/** Owners shown on the Insights pie, in the team's order. */
export const SUPPORT_OWNER_PIE_ORDER = ['BN', 'BP', 'AK', 'AV', 'JR', 'MM', 'Team'];

/** True when Victory Live's message is the latest one in the thread, so we owe them a reply. */
export const needsReply = (t: VlSupportTicketRow): boolean => {
  // A completed ticket is done: it never owes Victory Live a reply, whatever the thread looks like.
  if (t.vst_status && t.vst_status !== 'open') return false;
  if (!t.vst_last_inbound_at) return false;
  const inbound = new Date(t.vst_last_inbound_at).getTime();
  const latest = t.vst_last_message_at ? new Date(t.vst_last_message_at).getTime() : inbound;
  return Number.isFinite(inbound) && inbound >= latest;
};

/** Pie data: every listed name in order (zeros kept for the legend), plus "Other" for anything else. */
export function pieCounts(
  rows: Array<{ key: string; count: number }>,
  order: string[],
): Array<{ key: string; count: number }> {
  const byKey = new Map(rows.map((r) => [r.key, r.count]));
  const out = order.map((key) => ({ key, count: byKey.get(key) || 0 }));
  const other = rows.filter((r) => !order.includes(r.key)).reduce((sum, r) => sum + r.count, 0);
  return other ? [...out, { key: 'Other', count: other }] : out;
}

export type SupportSortKey = 'default' | 'event' | 'type' | 'owner' | 'opened' | 'duration' | 'lastActivity';
export type SortDir = 'asc' | 'desc';

/** "1/4/2027 7:15 PM" -> timestamp; anything unparseable ("Multiple dates") -> null. */
export const parseEventDate = (text: string | null | undefined): number | null => {
  if (!text) return null;
  const time = new Date(text).getTime();
  return Number.isFinite(time) ? time : null;
};

const ts = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  return Number.isFinite(time) ? time : null;
};

/**
 * Sorts the Support Tickets table. Empty values always go last. "default" puts
 * tickets that need a reply first, then the most recent activity.
 */
export function sortSupportTickets(
  rows: VlSupportTicketRow[],
  key: SupportSortKey,
  dir: SortDir,
  now: Date = new Date(),
): VlSupportTicketRow[] {
  const value = (t: VlSupportTicketRow): number | string | null => {
    switch (key) {
      case 'event':
        return parseEventDate(t.vst_event_date);
      case 'type':
        return SUPPORT_TYPE_LABELS[t.vst_type] || t.vst_type;
      case 'owner':
        return (t.vst_owners && t.vst_owners[0]) || t.vst_owner_initials || null;
      case 'opened':
        return ts(t.vst_opened_at);
      case 'duration':
        return ticketOpenSeconds(t, now);
      case 'lastActivity':
        return ts(t.vst_last_message_at || t.vst_opened_at);
      default:
        return null;
    }
  };
  const copy = [...rows];
  if (key === 'default') {
    return copy.sort((a, b) => {
      const reply = Number(needsReply(b)) - Number(needsReply(a));
      if (reply) return reply;
      const unread = Number(b.vst_unread_count || 0) - Number(a.vst_unread_count || 0);
      if (unread) return unread;
      return (ts(b.vst_last_message_at || b.vst_opened_at) || 0) - (ts(a.vst_last_message_at || a.vst_opened_at) || 0);
    });
  }
  const sign = dir === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb)) * sign;
    return (va - vb) * sign;
  });
}

export type InboxStatusLike = {
  configured: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  consecutiveFailures: number;
  lastError?: string | null;
  trackingAvailable?: boolean | null;
};

/** How stale the last good inbox check may be before the team is warned (ms). */
export const INBOX_STALE_MS = 10 * 60 * 1000;

/**
 * Warning to show when Victory Live replies might not be reaching the dashboard,
 * or null when the inbox check is healthy.
 */
export function inboxWarning(status: InboxStatusLike | null, now: Date = new Date()): string | null {
  if (!status) return null;
  if (status.trackingAvailable === false) {
    return "Support ticket tracking is temporarily unavailable and is being restored automatically. Wait for this to clear before sending tickets. Nothing already saved is lost.";
  }
  if (!status.configured) {
    return "Reply tracking isn't set up, so Victory Live replies won't appear here. Check your email for replies until it is.";
  }
  const lastOk = status.lastSuccessAt ? new Date(status.lastSuccessAt).getTime() : null;
  const stale = lastOk !== null && now.getTime() - lastOk > INBOX_STALE_MS;
  if (status.consecutiveFailures >= 3 || stale || (lastOk === null && status.consecutiveFailures >= 1)) {
    return "We can't check the support inbox right now, so Victory Live replies may be delayed. Check your email until this clears.";
  }
  return null;
}

/** Short label for the account a barcode ticket says we tried: "SSM · Houston Texans (Ticketmaster)". */
export function syncAccountsSummary(accounts: VlSupportTicketRow['vst_sync_accounts']): string | null {
  if (!accounts?.length) return null;
  const labels = Array.from(
    new Set(
      accounts.map((a) =>
        a.method === 'season_seat_manager' && a.team
          ? `${a.team} · ${groupLabel(syncGroupOf(a as SyncAccountLike))}`
          : groupLabel(syncGroupOf(a as SyncAccountLike)),
      ),
    ),
  );
  return labels.join(', ');
}

/** Barcode pie slices in a fixed order: the 10 account types. Other groups follow when present. */
export const BARCODE_ACCOUNT_SLICES: Array<{ group: string; label: string; color: string }> = [
  { group: 'Season Seat Manager · Ticketmaster', label: 'Ticketmaster [SSM]', color: '#026cdf' },
  { group: 'Season Seat Manager · SeatGeek', label: 'SeatGeek [SSM]', color: '#ff5b49' },
  { group: 'Season Seat Manager · Paciolan', label: 'Paciolan [SSM]', color: '#7c3aed' },
  { group: 'Season Seat Manager · MLB Ballpark', label: 'MLB Ballpark [SSM]', color: '#0f766e' },
  { group: 'Season Seat Manager · AXS', label: 'AXS [SSM]', color: '#e0287a' },
  { group: 'Purchase Manager · Ticketmaster', label: 'Ticketmaster [PM]', color: '#7cb4f5' },
  { group: 'Purchase Manager · SeatGeek', label: 'SeatGeek [PM]', color: '#ffab9f' },
  { group: 'Purchase Manager · Paciolan', label: 'Paciolan [PM]', color: '#c4b5fd' },
  { group: 'Purchase Manager · AXS', label: 'AXS [PM]', color: '#f9a8d4' },
  { group: 'Purchase Manager · MLB Ballpark', label: 'MLB Ballpark [PM]', color: '#5eead4' },
];

const EXTRA_GROUP_LABELS: Record<string, string> = {
  'Purchase Manager': 'All [PM] accounts',
  'Season Seat Manager · Other': 'Other [SSM]',
  'Not recorded': 'Not recorded',
};
const EXTRA_SLICE_COLORS = ['#f59e0b', '#64748b', '#94a3b8', '#22c55e'];

export type SyncAccountLike = { method: 'season_seat_manager' | 'purchase_manager'; team?: string | null; platform: string | null };

/** Same grouping the backend uses, e.g. "Season Seat Manager · Ticketmaster" or "Purchase Manager · AXS". */
export const syncGroupOf = (a: SyncAccountLike): string => {
  const known = ['Ticketmaster', 'SeatGeek', 'Paciolan', 'AXS', 'MLB Ballpark'];
  if (a.method === 'purchase_manager') return a.platform && known.includes(a.platform) ? `Purchase Manager · ${a.platform}` : 'Purchase Manager';
  return `Season Seat Manager · ${a.platform && [...known, 'Other'].includes(a.platform) ? a.platform : 'Other'}`;
};

export const groupLabel = (group: string): string =>
  BARCODE_ACCOUNT_SLICES.find((s) => s.group === group)?.label || EXTRA_GROUP_LABELS[group] || group;

export type BarcodeFilters = { league: string; team: string; accountType: string };

type BarcodeTicket = {
  type: string;
  status: 'open' | 'completed';
  secondsOpen: number;
  syncAccounts?: SyncAccountLike[] | null;
};

/**
 * Barcode pie data from the Insights ticket list, narrowed by league, team and
 * account type. Tickets with no recorded account are left out.
 */
export function barcodeGroupsFromTickets(
  tickets: BarcodeTicket[],
  filters: BarcodeFilters,
  leagueOfTeam: Record<string, string>,
): Array<{ group: string; count: number; openCount: number; avgSecondsToComplete: number | null }> {
  const acc = new Map<string, { count: number; open: number; done: number[] }>();
  for (const t of tickets) {
    if (t.type !== 'barcode_sync') continue;
    const accounts = (t.syncAccounts || []).filter((a) => {
      if (filters.team && a.team !== filters.team) return false;
      if (filters.league && (!a.team || leagueOfTeam[a.team] !== filters.league)) return false;
      if (filters.accountType && syncGroupOf(a) !== filters.accountType) return false;
      return true;
    });
    const groups = new Set(accounts.map(syncGroupOf));
    for (const g of groups) {
      const e = acc.get(g) || { count: 0, open: 0, done: [] };
      e.count += 1;
      if (t.status === 'open') e.open += 1;
      else e.done.push(t.secondsOpen);
      acc.set(g, e);
    }
  }
  return Array.from(acc.entries()).map(([group, e]) => ({
    group,
    count: e.count,
    openCount: e.open,
    avgSecondsToComplete: e.done.length ? Math.round(e.done.reduce((a, b) => a + b, 0) / e.done.length) : null,
  }));
}

/** Every fixed slice (zeros kept for the legend) plus any other group that has requests. */
export function barcodeSlices(
  groups: Array<{ group: string; count: number; openCount: number; avgSecondsToComplete: number | null }>,
): Array<{ group: string; label: string; color: string; count: number; openCount: number; avgSecondsToComplete: number | null }> {
  const byGroup = new Map(groups.map((g) => [g.group, g]));
  const fixed = BARCODE_ACCOUNT_SLICES.map((s) => {
    const g = byGroup.get(s.group);
    return { ...s, count: g?.count ?? 0, openCount: g?.openCount ?? 0, avgSecondsToComplete: g?.avgSecondsToComplete ?? null };
  });
  const known = new Set(BARCODE_ACCOUNT_SLICES.map((s) => s.group));
  const extra = groups
    .filter((g) => !known.has(g.group) && g.group !== 'Not recorded' && g.count > 0)
    .map((g, i) => ({ ...g, label: groupLabel(g.group), color: EXTRA_SLICE_COLORS[i % EXTRA_SLICE_COLORS.length] }));
  return [...fixed, ...extra];
}

export type SupportAlertTicket = {
  vst_id: string;
  vst_subject?: string | null;
  vst_status: 'open' | 'completed' | string;
  vst_event_name?: string | null;
  vst_owner_initials?: string | null;
  vst_unread_count?: number | string | null;
  vst_last_inbound_at?: string | null;
  vst_last_message_at?: string | null;
};

/**
 * What the alerts need from the tickets that have an unread or unanswered Victory Live reply:
 * how many open tickets are waiting on us (the sidebar number), how many unread replies there are
 * in total (a new one means chime and notify), and the most recent reply's ticket for the message.
 */
export function summarizeSupportAlerts(tickets: SupportAlertTicket[]) {
  const waiting = tickets.filter(
    (t) => t.vst_status === 'open' && needsReply({ vst_last_inbound_at: t.vst_last_inbound_at, vst_last_message_at: t.vst_last_message_at } as VlSupportTicketRow),
  );
  const unreadReplies = tickets.reduce((sum, t) => sum + Number(t.vst_unread_count || 0), 0);
  const latest = [...tickets]
    .filter((t) => t.vst_last_inbound_at)
    .sort((a, b) => String(b.vst_last_inbound_at).localeCompare(String(a.vst_last_inbound_at)))[0];
  return { needsReplyCount: waiting.length, unreadReplies, latest: latest || null };
}
